import { CORE_PERMISSIONS, permissionDependencies, USER_TYPE, } from "../constants/index.js";
import { PERMISSION_METADATA } from "../constants/permissions/permissionMetadata.js";
import { PERMISSION_AVAILABILITY } from "../constants/index.js";
/**
 * Builds permission objects based on provided keys and metadata.
 */
export const buildPermissions = (permissionKeys, permissionMetadata, entityNames, options = {}) => {
    const { isExternalUser = false, featureFlags = {} } = options;
    const permissions = {};
    permissionKeys.forEach((key) => {
        const metadata = permissionMetadata[key];
        if (!metadata)
            return;
        // Skip permissions that depend on disabled features
        if (key.includes("COMPANIES") && featureFlags.companies === false) {
            return;
        }
        // Quick start is skipped if AI features are off
        if ([CORE_PERMISSIONS.VIEW_QUICK_START].includes(key) && featureFlags.aiFeatures === false) {
            return;
        }
        let name = metadata.nameTemplate;
        // Use the description based on user type if available
        let description = metadata.descriptionTemplate;
        if (!description)
            return;
        // Replace entity name placeholders
        if (metadata.entityKeys) {
            metadata.entityKeys.forEach((entityKey) => {
                const entityName = entityNames[entityKey];
                if (entityName) {
                    const regex = new RegExp(`\\{${entityKey}\\}`, "g");
                    name = name.replace(regex, entityName);
                    description = description?.replace(regex, entityName) || "";
                    // Handle special case for lowercase
                    const lowercaseRegex = new RegExp(`\\{${entityKey}\\.toLowerCase\\(\\)\\}`, "g");
                    description =
                        description?.replace(lowercaseRegex, entityName.toLowerCase()) ||
                            "";
                }
            });
        }
        permissions[key] = {
            key,
            name,
            description,
            group: metadata.group,
            ...(metadata.subPermission ? { subPermission: true } : {}),
        };
    });
    return permissions;
};
/**
 * Check if a permission is available for a user type
 * @param permission The permission to check
 * @param userType The user type to check for
 * @returns Whether the permission is available for the user type
 */
function isPermissionAvailableForUserType(permission, userType) {
    return PERMISSION_AVAILABILITY[permission]?.includes(userType) || false;
}
/**
 * Get permission metadata adapted for a specific user type
 * @param userType The type of user to get metadata for
 * @returns Permission metadata for the specified user type
 */
export function getPermissionMetadata(userType) {
    const metadata = {};
    Object.entries(PERMISSION_METADATA).forEach(([key, permission]) => {
        if (!isPermissionAvailableForUserType(key, userType)) {
            return;
        }
        // Create a copy of the permission metadata
        const permissionCopy = { ...permission };
        // If there's a user-type specific description, use it
        if (permission.descriptionByUserType &&
            userType in permission.descriptionByUserType) {
            permissionCopy.descriptionTemplate =
                permission.descriptionByUserType[userType];
            delete permissionCopy.descriptionByUserType;
        }
        metadata[key] = permissionCopy;
    });
    return metadata;
}
/**
 * Gets all permissions that should be enabled when a permission is checked or unchecked
 * Uses the permissionDependencies to determine relationships between permissions
 */
export const getNewPermissions = (key, checked, dependencies = permissionDependencies) => {
    const newPermissions = { [key]: checked };
    if (checked && dependencies[key]) {
        dependencies[key].forEach((depKey) => {
            newPermissions[depKey] = true;
            // Get the dependencies of the dependency recursively
            const subDependencies = getNewPermissions(depKey, true, dependencies);
            Object.assign(newPermissions, subDependencies);
        });
    }
    return newPermissions;
};
/**
 * Determines if a permission checkbox should be disabled (forced checked)
 * This happens when a parent permission that depends on it is already checked
 */
export const isPermissionDisabled = (key, checkedPermissions = {}, dependencies = permissionDependencies) => {
    for (const [parentKey, deps] of Object.entries(dependencies)) {
        if (deps.includes(key) && checkedPermissions[parentKey]) {
            return true;
        }
    }
    return false;
};
/**
 * Gets permissions available for a specific user type
 * @param userType The user type to get permissions for
 * @returns Object containing permission keys available for the user type
 */
export function getPermissionsForUserType(userType) {
    const permissions = {};
    Object.entries(CORE_PERMISSIONS).forEach(([key, value]) => {
        const availability = PERMISSION_AVAILABILITY[value];
        if (availability && availability.includes(userType)) {
            permissions[key] = value;
        }
    });
    return permissions;
}
/**
 * Extracts all permissions from a user's access roles
 * @param user User object with AccessRoles
 * @returns Array of permission strings
 */
export const getUserPermissions = (user) => {
    if (!user || !Array.isArray(user.AccessRoles))
        return [];
    const permissions = new Set();
    user.AccessRoles.forEach((accessRole) => {
        try {
            const rolePermissions = Array.isArray(accessRole.permissions)
                ? accessRole.permissions
                : JSON.parse(accessRole.permissions || "[]");
            rolePermissions.forEach((permission) => permissions.add(permission));
        }
        catch (error) {
            console.error("Error parsing permissions:", error);
        }
    });
    return Array.from(permissions);
};
/**
 * Checks if a user has a specific permission
 * @param user User object with AccessRoles
 * @param permission Permission key to check
 * @returns True if user has the permission, false otherwise
 */
export const hasPermission = (user, permission) => {
    if (!user || !permission)
        return false;
    const userPermissions = getUserPermissions(user);
    return userPermissions.includes(permission);
};
/**
 * Checks if a user has any of the specified permissions
 * @param user User object with AccessRoles
 * @param permissions Array of permission keys to check
 * @returns True if user has any of the permissions, false otherwise
 */
export const hasAnyPermission = (user, permissions = []) => {
    if (!user || !permissions.length)
        return false;
    const userPermissions = getUserPermissions(user);
    return permissions.some((permission) => userPermissions.includes(permission));
};
/**
 * Checks if a user has all of the specified permissions
 * @param user User object with AccessRoles
 * @param permissions Array of permission keys to check
 * @returns True if user has all of the permissions, false otherwise
 */
export const hasAllPermissions = (user, permissions = []) => {
    if (!user || !permissions.length)
        return false;
    const userPermissions = getUserPermissions(user);
    return permissions.every((permission) => userPermissions.includes(permission));
};
/**
 * Checks if a user has permission based on their type
 * @param options Object containing user data and valid permissions for different user types
 * @returns True if user has any of the valid permissions for their user type
 */
export const userHasPermission = ({ userData, validPermissionsAdmin = [], validPermissionsManager = [], validPermissionsExternalUser = [], }) => {
    if (!userData || !userData.type)
        return false;
    let validPermissions = [];
    // Select the appropriate permissions based on user type
    switch (userData.type) {
        case USER_TYPE.ADMIN:
            validPermissions = validPermissionsAdmin;
            break;
        case USER_TYPE.MANAGER:
            validPermissions = validPermissionsManager;
            break;
        case USER_TYPE.EXTERNAL_USER:
            validPermissions = validPermissionsExternalUser;
            break;
        default:
            return false;
    }
    // If no permissions specified for this user type, allow by default
    if (validPermissions.length === 0)
        return true;
    // Check if the user has any of the valid permissions
    return hasAnyPermission(userData, validPermissions);
};
/**
 * Utility to create permission checking functions with a predefined user
 * Useful in components to avoid passing the user object each time
 * @param user User object to check permissions against
 * @returns Object with permission checking functions
 */
export const createPermissionChecker = (userOrPermissions) => {
    // If it is an array, use it directly; otherwise, extract permissions from the user
    const permissions = Array.isArray(userOrPermissions)
        ? userOrPermissions
        : getUserPermissions(userOrPermissions);
    return {
        check: (permission) => permissions.includes(permission),
        checkAny: (perms) => perms.some((p) => permissions.includes(p)),
        checkAll: (perms) => perms.every((p) => permissions.includes(p)),
        getUserPermissions: () => permissions,
        // Invoicing and Payments permissions
        canManageInvoicingAndPaymentsConfig: () => permissions.includes(CORE_PERMISSIONS.MANAGE_INVOICING_AND_PAYMENTS_SYSTEM_CONFIG),
        canManageActivityItems: () => permissions.includes(CORE_PERMISSIONS.MANAGE_ACTIVITY_ITEMS),
        canViewInvoices: () => permissions.includes(CORE_PERMISSIONS.VIEW_INVOICES),
        canCreateInvoice: () => permissions.includes(CORE_PERMISSIONS.CREATE_INVOICE),
        canEditInvoice: () => permissions.includes(CORE_PERMISSIONS.EDIT_INVOICE),
        canDeleteInvoice: () => permissions.includes(CORE_PERMISSIONS.DELETE_INVOICE),
        canChangeInvoiceStatus: () => permissions.includes(CORE_PERMISSIONS.CHANGE_INVOICE_STATUS),
        canExportSendInvoices: () => permissions.includes(CORE_PERMISSIONS.EXPORT_AND_SEND_INVOICE),
        canViewPaymentHistory: () => permissions.includes(CORE_PERMISSIONS.VIEW_PAYMENT_HISTORY),
        canRefundPayment: () => permissions.includes(CORE_PERMISSIONS.REFUND_PAYMENT),
        canPerformCharge: () => permissions.includes(CORE_PERMISSIONS.PERFORM_CHARGE),
        canDeleteCharge: () => permissions.includes(CORE_PERMISSIONS.DELETE_CHARGE),
    };
};
//# sourceMappingURL=permission.js.map